import axios from "axios";
import { getActiveWorkspaceId } from "@/lib/active-workspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach X-Workspace-Id to every outbound request whenever the user has an
// active workspace. Endpoints that don't care (auth, billing, /workspaces
// itself) simply ignore it on the backend.
api.interceptors.request.use((config) => {
  const wsId = getActiveWorkspaceId();
  if (wsId) {
    config.headers = config.headers || {};
    config.headers["X-Workspace-Id"] = wsId;
  }
  return config;
});

/**
 * Global 401 handler. If a request to a protected endpoint returns 401
 * AND we're currently inside the authenticated app shell, bounce to /login
 * with a friendly message. Auth endpoints handle their own 401s locally.
 */
const AUTH_ENDPOINTS_RE = /\/auth\/(login|register|verify-email|reset-password|forgot-password|oauth\/|google\/session|providers)/;

/**
 * Auto-retry transient upstream errors (502 / 503 / 504) with exponential
 * backoff. Applies to idempotent verbs only (GET / HEAD / OPTIONS) so we
 * never re-submit a payment or a POST twice. Most hot-reload / kubelet
 * blips self-heal after ~1s, so this makes them invisible to the user.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 2;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config;
    const method = (cfg?.method || "get").toLowerCase();
    const isIdempotent = method === "get" || method === "head" || method === "options";
    // Network errors (no response) look similar to a 502 for the user; retry
    // them too but only for idempotent verbs.
    const isNetworkError = !err?.response && err?.code !== "ECONNABORTED";
    if (cfg && isIdempotent && (TRANSIENT_STATUSES.has(status) || isNetworkError)) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= MAX_RETRIES) {
        const delay = 400 * Math.pow(2, cfg.__retryCount - 1); // 400ms, 800ms
        await new Promise((r2) => setTimeout(r2, delay));
        return api(cfg);
      }
    }
    return Promise.reject(err);
  },
);

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Loud console warning when a request is CSRF-blocked so operators notice
    // hostname/env misconfigs immediately instead of chasing silent failures.
    if (
      status === 403 &&
      typeof err?.response?.data?.detail === "string" &&
      err.response.data.detail.startsWith("Cross-site request blocked")
    ) {
      console.warn(
        "[api] CSRF block on",
        url,
        ",  check backend CSRF_TRUSTED_HOSTS / FRONTEND_URL.",
      );
    }
    if (status === 401 && !AUTH_ENDPOINTS_RE.test(url)) {
      // Only redirect when we're already inside /app/*
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/app/")) {
        // Prevent redirect loops
        if (!window.location.search.includes("session_expired")) {
          window.location.replace("/login?error=session_expired");
        }
      }
    }
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------------------
// Global unhandled-rejection safety net.
// If a component forgets to `.catch()` an axios call and the backend hiccups
// (502 during hot-reload, brief 503 during a redeploy), we don't want the
// CRA red-error overlay to blow up the whole app. Swallow transient errors
// silently, log everything else so we can still spot real bugs in the console.
// ---------------------------------------------------------------------------
if (typeof window !== "undefined" && !window.__ORReadyUnhandledRejectionInstalled) {
  window.__ORReadyUnhandledRejectionInstalled = true;
  window.addEventListener("unhandledrejection", (event) => {
    const err = event?.reason;
    const status = err?.response?.status;
    const isAxiosError = err?.isAxiosError || err?.name === "AxiosError";
    if (!isAxiosError) return;
    // Suppress the red overlay for transient upstream statuses and network
    // blips. Callers still receive the rejection and can handle it locally.
    if (
      TRANSIENT_STATUSES.has(status)
      || (!err?.response && err?.code !== "ECONNABORTED")
    ) {
      event.preventDefault();
      console.warn("[api] transient upstream error suppressed:", status || err?.code || "network", err?.config?.url);
    }
  });
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

export default api;
